import type { QrTargets } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { Download, ExternalLink, QrCode } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/app-shell';
import { Alert, Button, Card, EmptyState, SkeletonRows } from '@/components/ui';
import { apiGet } from '@/hooks/admin';
import { http } from '@/lib/api';
import { messageForError } from '@/lib/errors';
import { qk } from '@/lib/query';
import { useLocale } from '@/providers/locale';
import { useToast } from '@/providers/toast';

export function QrPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { notify } = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const query = useQuery({
    queryKey: qk.qrTargets,
    queryFn: () => apiGet<QrTargets>('/admin/qr/targets'),
  });

  /**
   * The code endpoints answer with image bytes, not an envelope — and they are
   * authenticated by the in-memory access token, which a plain link cannot
   * carry. So the file is fetched through the same client as everything else
   * and handed to the browser as a blob.
   */
  async function download(path: string, filename: string, format: 'png' | 'svg') {
    setBusy(`${path}:${format}`);
    try {
      const response = await http.get<Blob>(path, {
        params: { format },
        responseType: 'blob',
      });
      const href = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = href;
      anchor.download = `${filename}.${format}`;
      anchor.click();
      URL.revokeObjectURL(href);
    } catch (error) {
      notify(messageForError(t, error), 'danger');
    } finally {
      setBusy(null);
    }
  }

  const targets = query.data?.data;

  return (
    <>
      <PageHeader title={t('qr.title')} description={t('qr.subtitle')} />

      {query.isLoading ? (
        <SkeletonRows rows={3} />
      ) : !targets ? (
        <Alert tone="danger">{t('common.somethingBroke')}</Alert>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title={t('qr.general')}>
            <TargetRow
              target={targets.general.target}
              busyKey={busy}
              path="/admin/qr/general"
              filename="diora-menu"
              onDownload={download}
            />
          </Card>

          <Card title={t('nav.branches')}>
            {targets.branches.length === 0 ? (
              <EmptyState icon={<QrCode aria-hidden="true" />} title={t('qr.empty')} hint={t('qr.subtitle')} />
            ) : (
              <ul className="flex flex-col gap-4">
                {targets.branches.map((branch) => (
                  <li key={branch.id}>
                    <p className="font-medium">
                      {branch.name[locale] || branch.name.ar || branch.name.en}
                    </p>
                    <TargetRow
                      target={branch.target}
                      busyKey={busy}
                      path={`/admin/qr/branches/${branch.id}`}
                      filename={`diora-branch-${branch.id}`}
                      onDownload={download}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </>
  );
}

function TargetRow({
  target,
  path,
  filename,
  busyKey,
  onDownload,
}: {
  target: string;
  path: string;
  filename: string;
  busyKey: string | null;
  onDownload: (path: string, filename: string, format: 'png' | 'svg') => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* A URL is never mirrored, whatever the interface direction. */}
      <code className="dio-meta min-w-0 flex-1 truncate" dir="ltr">
        {target}
      </code>
      <a href={target} target="_blank" rel="noreferrer" className="dio-btn dio-btn--ghost dio-btn--sm">
        <ExternalLink className="dio-icon" aria-hidden="true" />
        {t('qr.openTarget')}
      </a>
      <Button size="sm" loading={busyKey === `${path}:png`} onClick={() => onDownload(path, filename, 'png')}>
        <Download className="dio-icon" aria-hidden="true" />
        PNG
      </Button>
      <Button size="sm" loading={busyKey === `${path}:svg`} onClick={() => onDownload(path, filename, 'svg')}>
        <Download className="dio-icon" aria-hidden="true" />
        SVG
      </Button>
    </div>
  );
}
